/**
 * Діалог створення/редагування кастомної ролі (Порція Б.5).
 *
 * Створення (role=null) — майстер у 2 кроки:
 *   Крок 1: вибір системної ролі-основи (з канонічних 11 дозволених основ —
 *           це ПIДМНОЖИНА системних ролей, не всі; решта системних у БД є,
 *           але як основа копіювання не дозволені)
 *   Крок 2: name + матриця галочок permissions
 *
 * Редагування (role=AccessRole) — лише крок 2 (основу не можна
 * змінити після створення).
 *
 * Правила UI (віддзеркалює backend):
 *  - 7 заборонених permissions з FORBIDDEN_IN_CUSTOM_ROLES НЕ
 *    показуються у формі взагалі. Якщо backend їх отримає — 422
 *    (захисний список). UI просто не дає їх вибрати.
 *  - Мертві/резервні з COSMETIC_PERMISSIONS — показуються СІРИМ з
 *    підписом «не enforce-иться». Можна вибрати, але OWNER має знати
 *    що галочка не діє.
 *  - Основа — лише з канонічних 11 дозволених системних ролей-основ
 *    (підмножина, не всі системні). Кастомні не у списку
 *    (Variant A: ланцюги заборонені).
 *
 * При помилці від backend (422/403) — humanizeBackendError() для
 * читабельного повідомлення.
 */
import { useState, useEffect, useMemo } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  accessRoles,
  CANONICAL_SYSTEM_ROLE_CODES,
  FORBIDDEN_IN_CUSTOM_ROLES,
  COSMETIC_PERMISSIONS,
  type AccessRole,
} from '@/api/admin';
import { humanizeBackendError } from '@/utils/backendError';
import { moduleName } from '@/utils/moduleNames';

interface Props {
  role: AccessRole | null;          // null = створення; AccessRole = редагування
  systemRoles: AccessRole[];          // системні ролі з RolesSection (вже відсортовані)
  onClose: () => void;
  onSaved: () => void;
}

export default function RoleEditorDialog({ role, systemRoles, onClose, onSaved }: Props) {
  const isEdit = role !== null;
  const [step, setStep] = useState<1 | 2>(isEdit ? 2 : 1);
  const [sourceCode, setSourceCode] = useState<string>(
    isEdit ? (role!.created_from_role_code ?? 'ACCOUNTANT') : ''
  );
  const [name, setName] = useState<string>(isEdit ? role!.name : '');
  const [permissions, setPermissions] = useState<Set<string>>(
    isEdit ? new Set(role!.permissions) : new Set()
  );
  const [error, setError] = useState<string | null>(null);

  // Тільки канонічні 11 дозволених основ (підмножина системних ролей),
  // навіть якщо backend через витік повернув би більше системних
  // (defense-in-depth на UI; backend теж блокує).
  const availableSources = useMemo(
    () => systemRoles.filter((r) => CANONICAL_SYSTEM_ROLE_CODES.includes(r.code)),
    [systemRoles]
  );

  // При зміні source — попередньо встановлюємо галочки з системної ролі-основи.
  useEffect(() => {
    if (!isEdit && sourceCode) {
      const src = systemRoles.find((r) => r.code === sourceCode);
      if (src) {
        // Фільтруємо forbidden — навіть якщо у системній є adminів, кастомна
        // НЕ повинна їх успадкувати (хоча системні їх і не мають насправді).
        const initial = src.permissions.filter(
          (p) => !FORBIDDEN_IN_CUSTOM_ROLES.includes(p)
        );
        setPermissions(new Set(initial));
      }
    }
  }, [sourceCode, systemRoles, isEdit]);

  // Усі permissions окрім forbidden — для матриці галочок.
  const { data: allPerms } = useQuery({
    queryKey: ['all-permissions'],
    queryFn: async () => {
      // Беремо з системної AUDITOR (має найбільший read-only набір)
      // + об'єднання з усіх системних. Альтернатива — окремий endpoint
      // /access-permissions; тут уникаємо нової залежності.
      const all = new Set<string>();
      for (const r of systemRoles) {
        for (const p of r.permissions) all.add(p);
      }
      return Array.from(all).sort();
    },
    enabled: systemRoles.length > 0,
  });

  // Довідник прав (code → українська назва + модуль) для показу назв замість
  // сирих кодів. Кешуємо назавжди (системний довідник, не змінюється у сесії).
  const { data: permCatalog } = useQuery({
    queryKey: ['permissions-catalog'],
    queryFn: () => accessRoles.permissionsCatalog(),
    staleTime: Infinity,
  });
  const permMeta = useMemo(() => {
    const m = new Map<string, string>();
    for (const it of permCatalog ?? []) m.set(it.code, it.name);
    return m;
  }, [permCatalog]);

  // Permissions для показу: всі - 7 forbidden (вони не у формі).
  const displayablePerms = useMemo(() => {
    if (!allPerms) return [];
    return allPerms.filter((p) => !FORBIDDEN_IN_CUSTOM_ROLES.includes(p));
  }, [allPerms]);

  // Групуємо за модулем для зручності.
  const grouped = useMemo(() => {
    const g: Record<string, string[]> = {};
    for (const p of displayablePerms) {
      const module = p.split('.')[0] ?? 'other';
      (g[module] ??= []).push(p);
    }
    return g;
  }, [displayablePerms]);

  const saveMut = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        return accessRoles.patch(role!.id, {
          name,
          permissions: Array.from(permissions),
        });
      }
      return accessRoles.create({
        name,
        source_system_role_code: sourceCode,
        permissions: Array.from(permissions),
      });
    },
    onSuccess: onSaved,
    onError: (e) => setError(humanizeBackendError(e)),
  });

  function togglePerm(code: string) {
    setPermissions((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  }

  function canSubmit() {
    if (!name.trim()) return false;
    if (!isEdit && !sourceCode) return false;
    return true;
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 50,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white', padding: '1.5rem', borderRadius: 'var(--radius)',
          width: '720px', maxWidth: '90vw', maxHeight: '90vh', overflowY: 'auto',
        }}
      >
        <h3 style={{ marginTop: 0 }}>
          {isEdit ? `Редагування ролі «${role!.name}»` : 'Створити власну роль'}
        </h3>

        {/* Крок 1: вибір основи (тільки при створенні) */}
        {!isEdit && step === 1 && (
          <>
            <div style={{ marginBottom: '1rem' }}>
              Виберіть системну роль як основу. Можна змінити галочки далі.
            </div>
            <div style={{ display: 'grid', gap: '0.5rem' }}>
              {availableSources.map((r) => (
                <label
                  key={r.id}
                  style={{
                    display: 'flex', padding: '0.75rem', border: '1px solid var(--border)',
                    borderRadius: 'var(--radius)', cursor: 'pointer',
                    background: sourceCode === r.code ? 'var(--accent-light)' : 'white',
                  }}
                >
                  <input
                    type="radio"
                    name="source"
                    value={r.code}
                    checked={sourceCode === r.code}
                    onChange={() => setSourceCode(r.code)}
                    style={{ marginRight: '0.75rem' }}
                  />
                  <div>
                    <div style={{ fontWeight: 600 }}>{r.name}</div>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
                      {r.permissions.length} прав · code: <code>{r.code}</code>
                    </div>
                  </div>
                </label>
              ))}
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between' }}>
              <button type="button" onClick={onClose}>Скасувати</button>
              <button
                type="button"
                className="primary"
                disabled={!sourceCode}
                onClick={() => setStep(2)}
              >
                Далі →
              </button>
            </div>
          </>
        )}

        {/* Крок 2: name + permissions */}
        {step === 2 && (
          <>
            <div style={{ marginBottom: '1rem' }}>
              <label>Назва ролі:</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                style={{ width: '100%', padding: '0.5rem', marginTop: '0.25rem' }}
                placeholder="Наприклад: Помічник бухгалтера"
              />
              {!isEdit && (
                <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginTop: '0.25rem' }}>
                  На основі: <strong>{systemRoles.find((r) => r.code === sourceCode)?.name ?? sourceCode}</strong>
                </div>
              )}
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <strong>Permissions ({permissions.size}):</strong>
              <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
                7 адміністративних/закриття-періоду permissions не показуються —
                вони заборонені у кастомних ролях.
                <br />
                <span style={{ color: 'var(--text-muted, #999)' }}>Сірі</span> — не enforce-яться зараз (резерв).
              </div>
              {Object.entries(grouped).map(([module, perms]) => (
                <fieldset key={module} style={{ marginBottom: '0.5rem', padding: '0.5rem', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                  <legend style={{ padding: '0 0.5rem', fontWeight: 600 }}>{moduleName(module)}</legend>
                  {perms.map((p) => {
                    const isCosmetic = COSMETIC_PERMISSIONS.includes(p);
                    // Назва українською (крупно) + код сірим дрібним. Fallback
                    // на код, якщо довідник ще не завантажився / коду нема.
                    const label = permMeta.get(p) ?? p;
                    return (
                      <label
                        key={p}
                        style={{
                          display: 'block', padding: '0.25rem 0',
                          opacity: isCosmetic ? 0.5 : 1,
                          color: isCosmetic ? 'var(--text-muted, #999)' : 'inherit',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={permissions.has(p)}
                          onChange={() => togglePerm(p)}
                          style={{ marginRight: '0.5rem' }}
                        />
                        <span style={{ fontWeight: 500 }}>{label}</span>
                        <code style={{ fontSize: '0.7rem', color: 'var(--text-muted, #999)', marginLeft: '0.4rem' }}>
                          {p}
                        </code>
                        {isCosmetic && (
                          <span style={{ fontSize: '0.75rem', marginLeft: '0.5rem', fontStyle: 'italic' }}>
                            (не enforce-иться)
                          </span>
                        )}
                      </label>
                    );
                  })}
                </fieldset>
              ))}
            </div>

            {error && (
              <div style={{ background: 'var(--error-bg, #fee)', color: 'var(--error, #c00)', padding: '0.5rem 0.75rem', borderRadius: 'var(--radius)', marginBottom: '0.5rem', whiteSpace: 'pre-wrap' }}>
                {error}
              </div>
            )}

            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'space-between' }}>
              <div>
                {!isEdit && (
                  <button type="button" onClick={() => setStep(1)}>← Назад</button>
                )}
              </div>
              <div>
                <button type="button" onClick={onClose} style={{ marginRight: '0.5rem' }}>
                  Скасувати
                </button>
                <button
                  type="button"
                  className="primary"
                  disabled={!canSubmit() || saveMut.isPending}
                  onClick={() => {
                    setError(null);
                    saveMut.mutate();
                  }}
                >
                  {saveMut.isPending ? 'Збереження…' : isEdit ? 'Зберегти' : 'Створити'}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
